import type { Update } from '@ngrx/entity';
import type { CoreSensor } from '../../../interfaces/Sensor.interface';
import { type Actions, ActionType } from './sensors.actions';
import { initialState, MapKey, sensorAdapter, type State } from './sensors.state';

export function sensorsReducer(state: State = initialState, action: Actions): State {
    switch (action.type) {
        case ActionType.RESET: {
            return initialState;
        }
        case ActionType.FETCH_SENSORS: {
            return { ...state, isPending: true, error: null };
        }
        case ActionType.FETCH_SENSORS_SUCCEEDED: {
            return sensorAdapter.setAll(action.payload.sensors, { ...state, isLoaded: true, isPending: false });
        }
        case ActionType.FETCH_SENSORS_FAILED: {
            return { ...state, isPending: false, error: action.payload.error };
        }
        case ActionType.UPDATE_SENSOR: {
            return sensorAdapter.updateOne(
                {
                    id: action.payload.timeseriesId,
                    changes: action.payload.sensorUpdate
                },
                state
            );
        }
        case ActionType.UPDATE_SENSORS: {
            return sensorAdapter.updateMany(
                action.payload.map(sensor => {
                    return { id: sensor.timeseriesId, changes: sensor.sensorUpdate };
                }),
                state
            );
        }
        case ActionType.CREATE_SENSOR: {
            return sensorAdapter.addOne(action.payload.sensor, { ...state, isLoaded: true, isPending: false });
        }
        case ActionType.SET_FAVORITE_SENSOR: {
            const sensors = sensorAdapter.getSelectors().selectEntities(state);
            const sensor = sensors[action.payload.timeseriesId];

            if (!sensor) {
                return state;
            }

            const selector = sensorAdapter.getSelectors().selectEntitiesByMappedId(MapKey.BY_BUILDING_ID, sensor.buildingId);
            const oldFavoriteSensor = selector(state).find(
                coreSensor => coreSensor.isFavorite === true && coreSensor.universeId === sensor.universeId
            );
            const sensorsToUpdate: Update<CoreSensor>[] = [
                {
                    id: action.payload.timeseriesId,
                    changes: {
                        isFavorite: true
                    }
                }
            ];

            if (oldFavoriteSensor) {
                sensorsToUpdate.push({
                    id: oldFavoriteSensor.timeseriesId,
                    changes: {
                        isFavorite: false
                    }
                });
            }

            return sensorAdapter.updateMany(sensorsToUpdate, state);
        }
        case ActionType.CHANGE_SENSORS_METER_ID: {
            const newSensorsTimeseries = action.payload.timeseriesIds;

            const sensorsSelector = sensorAdapter.getSelectors().selectEntitiesByMappedId(MapKey.BY_METER_ID, action.payload.meterId);
            const oldSensorsTimeseries = sensorsSelector(state)
                .filter(sensor => !newSensorsTimeseries.includes(sensor.timeseriesId))
                .map(sensor => sensor.timeseriesId);

            const sensorsToUpdate: Update<CoreSensor>[] = newSensorsTimeseries.map(newSensorTimeseries => {
                return {
                    id: newSensorTimeseries,
                    changes: {
                        meterId: action.payload.meterId
                    }
                };
            });

            if (oldSensorsTimeseries) {
                sensorsToUpdate.push(
                    ...oldSensorsTimeseries.map(oldSensorTimeseries => {
                        return {
                            id: oldSensorTimeseries,
                            changes: {
                                meterId: undefined as undefined
                            }
                        } satisfies Update<CoreSensor>;
                    })
                );
            }
            return sensorAdapter.updateMany(sensorsToUpdate, state);
        }

        case ActionType.UPDATE_SYSTEM_BUILDING: {
            const sensorsSelector = sensorAdapter.getSelectors().selectEntitiesByMappedId(MapKey.BY_SYSTEM_ID, action.payload.systemId);
            const sensors = sensorsSelector(state);
            if (sensors) {
                const sensorsToUpdate = sensors.map(sensor => {
                    return {
                        id: sensor.timeseriesId,
                        changes: {
                            buildingId: action.payload.buildingId
                        }
                    };
                });
                return sensorAdapter.updateMany(sensorsToUpdate, state);
            }
            return state;
        }

        default:
            return state;
    }
}
